/**
 * Library responsible for handling user font uploading logic
 * @author Muhammad Shahrukh <shahrukh@250mils.com>
 */

import {addUploadedFontToUserFonts} from "../components/font-selector/font-selector-slice.js";
import {initUpload, progressHandler} from "./upload-library.js";
import Emitter from '@/services/event-emitter';

const VALID_FONT_FILE_EXTENSIONS = ['ttf', 'otf'],
    FONT_UPLOAD_LOADING_KEY = 'userFontUpload';


/**
 * array of errors for an upload
 * @type {*[]}
 */
let uploadErrors = [];


/**
 * Initialises the upload flow for user fonts including events for the input field used to get the font files selected by a user
 * @param {jQuery} uploadFontInput the input field that will be used to prompt the user to upload font files
 */
export const initFontUpload = (uploadFontInput) => {
    initUpload(uploadFontInput, uploadFontFiles, onUnableToUploadFontsError);
}

/**
 * Filters the font files and opens up the user agreement modal with the filtered valid files (if any)
 * @param {Array} fontFiles
 * @param {function} onFontUploaded
 */
export const uploadFontFiles = (fontFiles, onFontUploaded) => {
    const validFiles = getValidFontFiles(fontFiles);
    if (validFiles.length !== fontFiles.length) {
        showInvalidFontFileError();
    }

    if (validFiles.length > 0) {
        PMW.openUploadFontModal({
            files: validFiles,
            onFontUploaded: onFontUploaded
        });
    }
}

/**
 *
 * Uploads all the given files one by one and updates redux store
 * @param files
 * @param onFontUploaded
 * @return {Promise<void>}
 */
export const startUpload = async (files, onFontUploaded) => {
    const numTotalFiles = files.length;

    for (let i = 0; i < numTotalFiles; i++) {
        Emitter.emit('font:uploadStart', {fontsProcessed: i + 1, totalNumberOfFonts: numTotalFiles});

        try {
            let fontFile = new FormData();
            fontFile.append('Font', files[i]);
            const response = await getFontUploadAjax(fontFile);
            handleSuccessResponse(response, onFontUploaded);

        } catch (e) {
            handleUploadErrorResponse(e);
        }
    }

    if (uploadErrors.length > 0) {
        handleUploadErrorResponse(`error in processing fonts :: error details ${uploadErrors}`);
    }

    uploadErrors = [];
    Emitter.emit('font:uploadEnd');
}

/**
 * Add user fonts to redux store
 * @param {Object} serverResponse The response from the server after uploading font
 * @param {function} onFontUploaded callback to execute on success
 */
const handleSuccessResponse = (serverResponse, onFontUploaded) => {
    if (serverResponse.err) {
        handleErrorsFromSuccessResponse(serverResponse);
        return;
    }
    PMW.redux.store.dispatch(addUploadedFontToUserFonts(serverResponse));
    if (typeof onFontUploaded === 'function') {
        onFontUploaded(serverResponse.fontFamily, serverResponse.name);
    }
}

/**
 * if the user is non-premium then show the fonts premium upsell dialog
 * @param {Object} serverResponse
 */
const handleErrorsFromSuccessResponse = (serverResponse) => {
    if (serverResponse.err === 'premium') {
        PMW.showPremiumOnlyFeatureDialog(PMW.PREMIUM_ONLY_FEATURE_NAME_CUSTOM_FONT);
    } else {
        uploadErrors.push(serverResponse.name);
    }
}

/**
 * open the error modal on upload ajax error
 * @param {Error | string} error
 */
const handleUploadErrorResponse = (error) => {
    PMW.log(error);
    PMW.openErrorModal({
        message: i18next.t('pmwjs_upload_error'),
    });
}

/**
 *
 * @param fileData
 * @return {*}
 */
const getFontUploadAjax = (fileData) => {
    return PMW.write(PMW.util.site_url('font/upload'), fileData, null, 'json', {
        timeout: 7200000,
        xhr: progressHandler.bind(this, FONT_UPLOAD_LOADING_KEY),
        cache: false,
        processData: false,
        contentType: false,
    });
}

/**
 * Filters given list of font files and returns files with valid extensions
 * @param fontFiles
 * @return {*[]}
 */
export const getValidFontFiles = (fontFiles) => {
    let fileName, fileExtension;
    const validFontFiles = [];

    for (let file of fontFiles) {
        fileName = file.name;
        fileExtension = fileName.split('.')[fileName.split('.').length - 1].toLowerCase();
        if (VALID_FONT_FILE_EXTENSIONS.includes(fileExtension)) {
            validFontFiles.push(file);
        }
    }

    return validFontFiles;
}

/**
 * General error to show for invalid font file
 */
const showInvalidFontFileError = () => {
    PMW.openErrorModal({
        message: i18next.t('pmwjs_invalid_font_file_error')
    })
}

/**
 * general error to display if user browser does not support uploads
 */
export const onUnableToUploadFontsError = () => {
    PMW.openErrorModal({
        message: i18next.t('pmwjs_invalid_font_file_error')
    })
}
